import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@draftedge/ui';
import { purgeDeviceData } from '@/lib/deviceLicense';

/**
 * "More" page — native app only. The tab-bar slot it used to own is now
 * the Profile tab; this content lives on in the Home screen sidebar
 * (components/mobile/HomeSidebar.tsx), which imports the pieces below.
 * The route stays functional for muscle-memory deep links.
 */

export const MORE_LINKS: Array<{ to: string; label: string; blurb: string }> =
  [
    // Rankings traded places with My Cheatsheet: cheat sheets are a bottom
    // tab now, and the read-only expert board became a menu destination.
    { to: '/rankings', label: 'Rankings', blurb: 'The full expert board, 2026 PPR' },
    { to: '/compare', label: 'Compare Players', blurb: 'Two to four side by side, differences called out' },
    { to: '/rookies', label: 'Rookies', blurb: 'Incoming class rankings & profiles' },
    { to: '/trending', label: 'Waiver Wire', blurb: 'Adds & drops across all leagues' },
    { to: '/exposure', label: 'Exposure', blurb: 'Cross-league exposure via Sleeper' },
    { to: '/depth-charts', label: 'Depth Charts', blurb: 'Every team, every position' },
    { to: '/injuries', label: 'Injuries', blurb: 'League-wide injury report' },
    { to: '/help', label: 'Help', blurb: 'How every feature works' },
  ];

// Bump alongside the App Store marketing version (Xcode MARKETING_VERSION).
export const APP_VERSION = '1.0';

export default function More() {
  return (
    <main className="mx-auto max-w-lg px-4 pt-safe pb-6">
      <h1 className="font-display text-2xl">More Tools</h1>
      <ul className="mt-4 divide-y divide-line/50 overflow-hidden rounded-xl border border-line/50">
        {MORE_LINKS.map(({ to, label, blurb }) => (
          <li key={to}>
            <Link
              to={to}
              className="flex items-center justify-between gap-3 bg-surface-raised px-4 py-3.5 transition-colors hover:bg-surface-subtle active:bg-surface-subtle"
            >
              <span>
                <span className="block font-medium text-ink">{label}</span>
                <span className="block text-sm text-ink-muted">{blurb}</span>
              </span>
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4 flex-none text-ink-muted"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </Link>
          </li>
        ))}
      </ul>

      {/* App Store 5.1.1(v): a server-side account exists per device, so an
          in-app deletion control is mandatory. */}
      <ResetAppData />

      {/* Support + privacy URLs are owner TODOs before submission (App
          Store Connect requires both as metadata regardless). Add the two
          rows here once the real URLs exist; never ship guessed addresses:
          <a href={SUPPORT_URL}>Support</a> / <a href={PRIVACY_URL}>Privacy
          Policy</a>. */}
      <section aria-label="About" className="mt-8">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-subtle">
          About
        </h2>
        <p className="mt-2 text-sm text-ink-muted">
          DraftEdge for iOS · Version {APP_VERSION}
        </p>
        <p className="mt-0.5 text-xs text-ink-subtle">
          Rankings and projections update throughout draft season.
        </p>
      </section>
    </main>
  );
}

/**
 * "Reset app data" - erases the device account and everything it owns on
 * DraftEdge servers, then relaunches the app as a brand-new install. Two
 * taps required; the confirm spells out exactly what is deleted.
 * Exported for the Home sidebar (its new primary surface).
 */
export function ResetAppData() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const run = async () => {
    setBusy(true);
    setFailed(false);
    const ok = await purgeDeviceData();
    if (ok) {
      // Fresh start: reload boots the app with no identity; a new blank
      // device account provisions on the way up.
      window.location.replace('/');
      return;
    }
    setBusy(false);
    setFailed(true);
  };

  return (
    <section aria-label="Reset app data" className="mt-8">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-subtle">
        Your data
      </h2>
      {!confirming ? (
        <>
          <p className="mt-2 text-xs text-ink-muted">
            Drafts, cheat sheets, and your token balance are stored on
            DraftEdge servers under an anonymous id created for this device.
            Your Sleeper username and leagues stay on this phone.
          </p>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="mt-3 text-sm font-semibold text-brand-danger active:opacity-70"
          >
            Reset app data
          </button>
        </>
      ) : (
        <div className="mt-2 rounded-card border border-brand-danger/30 bg-brand-danger/10 p-4">
          <p className="text-sm font-semibold text-ink">
            Delete everything and start over?
          </p>
          <p className="mt-1 text-xs text-ink-muted">
            This permanently deletes your drafts, cheat sheet, notes,
            remaining Coach tokens, and your linked Sleeper account and
            leagues, from this device and from DraftEdge servers. It cannot
            be undone.
          </p>
          {failed ? (
            <p className="mt-2 text-xs font-semibold text-brand-danger">
              The delete did not go through. Check your connection and
              try again - nothing was removed.
            </p>
          ) : null}
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="dangerSolid"
              size="sm"
              disabled={busy}
              onClick={() => void run()}
            >
              {busy ? 'Deleting...' : 'Delete everything'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                setFailed(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
